import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

function ComparisonChart({ activities }) {
  const getDailyComparison = () => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayActivities = activities.filter((a) => a.timestamp.split('T')[0] === dateStr);
      const totalRisk = dayActivities.reduce((sum, a) => sum + (a.risk_score || 0), 0);
      last7Days.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        incidents: dayActivities.filter((a) => a.type === 'incident').length,
        risk: totalRisk,
        events: dayActivities.length,
      });
    }
    return last7Days;
  };

  const data = getDailyComparison();
  const today = data[data.length - 1];
  const yesterday = data[data.length - 2];

  const getTrend = () => {
    if (!yesterday) return { Icon: Minus, text: 'No comparison', variant: 'outline' };
    const change = today.risk - yesterday.risk;
    if (change > 10) return { Icon: TrendingUp, text: `+${change} risk`, variant: 'destructive' };
    if (change < -10) return { Icon: TrendingDown, text: `${change} risk`, variant: 'success' };
    return { Icon: Minus, text: 'Stable', variant: 'default' };
  };

  const trend = getTrend();
  const TrendIcon = trend.Icon;

  return (
    <Card>
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">7-Day Trend</CardTitle>
        <Badge variant={trend.variant}>
          <TrendIcon className="h-3 w-3" />
          {trend.text}
        </Badge>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis yAxisId="left" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.5rem',
                color: 'hsl(var(--foreground))',
              }}
            />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="risk"
              stroke="hsl(var(--destructive))"
              strokeWidth={2}
              name="Risk Score"
              dot={{ r: 4 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="incidents"
              stroke="hsl(var(--warning))"
              strokeWidth={2}
              name="Incidents"
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <div className="text-xs text-muted-foreground">Today</div>
            <div className="text-lg font-semibold text-foreground">{today?.events || 0}</div>
            <div className="text-xs text-muted-foreground">events</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Risk</div>
            <div className="text-lg font-semibold text-foreground">{today?.risk || 0}</div>
            <div className="text-xs text-muted-foreground">points</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Incidents</div>
            <div className="text-lg font-semibold text-foreground">{today?.incidents || 0}</div>
            <div className="text-xs text-muted-foreground">count</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ComparisonChart;
